import { BulkQueryEvalNamespaceRequest_EvaluationMode } from '@weave/schema-gen-ts/dist/apis/api/wetags/v1/api.pb';
import appConfig from '@frontend/env';
import { SchemaWeTagsService } from '@frontend/schema';
import { type WetagQuery, getQueryCacheKey } from './query-builder';
import type {
  WetagQueryResponse,
  WetagClientConfig,
  WetagBulkQueryResponse,
  WetagBulkQueryAggregationMode,
} from './types';

let config: WetagClientConfig | null = null;

export function initializeWetagClient(clientConfig: WetagClientConfig = {}): void {
  config = clientConfig;
}

export function getWetagConfig(): WetagClientConfig {
  return config || {};
}

export function isWetagClientInitialized(): boolean {
  return config !== null;
}

export interface ExecuteWetagQueryOptions {
  namespaceId: string;
  query: WetagQuery;
  entityId: string | undefined;
}

export async function executeWetagQuery(options: ExecuteWetagQueryOptions): Promise<WetagQueryResponse> {
  const { namespaceId, query, entityId } = options;

  const queryString = query._toQueryString(entityId ?? '');

  try {
    const response = await SchemaWeTagsService.QueryNamespace(
      {
        namespaceId,
        query: queryString,
      },
      { baseUrl: appConfig.BACKEND_API_V2 }
    );

    return response;
  } catch (error) {
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status: number }).status;
      if (status === 404) {
        console.warn('Wetag not found. Are you sure you created it?');
        return { result: false };
      }
    }
    return { result: false };
  }
}

export interface ExecuteWetagBulkQueryOptions {
  namespaceId: string;
  queries: WetagQuery[];
  strategy?: WetagBulkQueryAggregationMode;
}

export async function executeWetagBulkQuery(options: ExecuteWetagBulkQueryOptions): Promise<WetagBulkQueryResponse> {
  const queryStrings = options.queries.map((query) => query._toQueryString(''));

  try {
    const response = await SchemaWeTagsService.BulkQueryEvalNamespace(
      {
        namespaceId: options.namespaceId,
        queries: queryStrings,
        evaluationMode: parseBulkWetagEvaluationMode(options.strategy),
      },
      { baseUrl: appConfig.BACKEND_API_V2 }
    );
    return response;
  } catch (error) {
    console.error('Error executing bulk wetag query:', error);
    return {
      matched: false,
      error: error instanceof Error ? error.message : 'Unknown error attempting wetag bulk query',
    };
  }
}

function parseBulkWetagEvaluationMode(
  strategy: WetagBulkQueryAggregationMode | undefined
): BulkQueryEvalNamespaceRequest_EvaluationMode | undefined {
  if (strategy === 'ANY') {
    return BulkQueryEvalNamespaceRequest_EvaluationMode.ANY_TRUE;
  }
  if (strategy === 'ALL') {
    return BulkQueryEvalNamespaceRequest_EvaluationMode.ALL_TRUE;
  }

  return strategy;
}

const baseKey = 'wetags';

export const wetagQueryKeys = {
  all: [baseKey] as const,
  namespace: (namespaceId: string) => [baseKey, namespaceId] as const,
  entity: (namespaceId: string, entityId: string) => [baseKey, namespaceId, entityId] as const,
  query: (namespaceId: string, entityId: string, query: WetagQuery) =>
    [baseKey, namespaceId, entityId, getQueryCacheKey(query)] as const,
  queryBulk: (namespaceId: string, queries: WetagQuery[]) =>
    [baseKey, namespaceId, 'bulk', ...queries.map((query) => getQueryCacheKey(query))] as const,
  tag: (namespaceId: string, tagId: string, entityId: string) => [baseKey, namespaceId, tagId, entityId] as const,
};
